import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, Clock, ArrowRight, CheckCircle, Spinner, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { BUSINESS, SERVICE_TYPE_GROUPS } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackFormSubmit } from "@/hooks/useTrafficTracker";

const initial = {
  name: "",
  email: "",
  phone: "",
  appliance_type: "",
  service_type: "quote",
  preferred_date: "",
  address: "",
  description: "",
  bought_from_shop: false,
  referral_code: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid phone required";
    if (!form.appliance_type) e.appliance_type = "Pick a service";
    if (!form.service_type) e.service_type = "Repair or install?";
    if (!form.description.trim() || form.description.trim().length < 5) e.description = "Tell us a bit more";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Normalize the referral code (uppercase, trim). Empty stays empty.
      const codeClean = (payload.referral_code || "").trim().toUpperCase();
      payload.referral_code = codeClean || null;
      await api.post("/service-requests", payload);
      trackFormSubmit("service-request");
      toast.success(codeClean
        ? "Free quote request sent! Your $25 referral discount will be applied to your final bill."
        : "Free quote request sent! We'll be in touch shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      console.error(err);
      const msg = formatApiErrorDetail(err?.response?.data?.detail);
      toast.error(msg || "Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Tag size={12} weight="fill" /> Free estimates &amp; quotes
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Tell us the job —<br />
          <span className="text-[var(--ji-accent)] italic">we'll quote it free.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Appliances, courier runs, or home work — every quote is free and there's zero pressure. Use the form below or call directly. We answer Mon–Sat and respond to online requests within hours.
        </p>
      </section>

      {/* Form + Sidebar */}
      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-7">
          <div className="ji-card p-7 md:p-10" style={{ borderRadius: 4 }}>
            {submitted ? (
              <div data-testid="form-success" className="py-10 text-center">
                <CheckCircle size={44} weight="duotone" className="text-[var(--ji-accent)] mx-auto" />
                <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-4 text-3xl font-bold tracking-tight">Free quote on the way.</h2>
                <p className="mt-3 text-[var(--ji-muted)]">
                  We've got your request and will reach out shortly with a no-cost quote. For urgent issues, please call{" "}
                  <a className="text-[var(--ji-primary)] font-semibold" href={`tel:${BUSINESS.phones[0].tel}`}>{BUSINESS.phones[0].display}</a>.
                </p>
                <button
                  data-testid="form-new-request"
                  onClick={() => setSubmitted(false)}
                  className="ji-btn-outline mt-8 inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold"
                  style={{ borderRadius: 4 }}
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form data-testid="service-request-form" onSubmit={onSubmit} className="space-y-5">
                <div className="ji-overline inline-flex items-center gap-2">
                  <Tag size={12} weight="fill" /> Free Quote Request
                </div>
                <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight">
                  Get your free quote in 60 seconds.
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">Full name *</Label>
                    <Input id="name" data-testid="form-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 mt-2" placeholder="Jane Doe" />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">Phone *</Label>
                    <Input id="phone" data-testid="form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 mt-2" placeholder="(504) 555-0100" />
                    {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                    <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 mt-2" placeholder="you@email.com" />
                    {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium">Service needed *</Label>
                    <Select value={form.appliance_type} onValueChange={(v) => update("appliance_type", v)}>
                      <SelectTrigger data-testid="form-appliance" className="h-12 mt-2"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        {SERVICE_TYPE_GROUPS.map((grp) => (
                          <SelectGroup key={grp.label}>
                            <SelectLabel className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-accent)]">
                              {grp.label}
                            </SelectLabel>
                            {grp.options.map((a) => (
                              <SelectItem key={a} value={a} data-testid={`appliance-option-${a}`}>{a}</SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.appliance_type && <p className="text-xs text-red-600 mt-1">{errors.appliance_type}</p>}
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Type of request *</Label>
                    <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                      <SelectTrigger data-testid="form-service-type" className="h-12 mt-2"><SelectValue placeholder="Quote / repair / install…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quote" data-testid="service-option-quote">Free quote / estimate</SelectItem>
                        <SelectItem value="repair" data-testid="service-option-repair">Repair</SelectItem>
                        <SelectItem value="installation" data-testid="service-option-installation">Installation</SelectItem>
                        <SelectItem value="delivery" data-testid="service-option-delivery">Delivery</SelectItem>
                        <SelectItem value="courier" data-testid="service-option-courier">Courier run</SelectItem>
                        <SelectItem value="mounting" data-testid="service-option-mounting">Mounting</SelectItem>
                        <SelectItem value="removal" data-testid="service-option-removal">Removal / Haul</SelectItem>
                        <SelectItem value="diagnostic" data-testid="service-option-diagnostic">Diagnostic only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferred_date" className="text-sm font-medium">Preferred date</Label>
                    <Input id="preferred_date" type="date" data-testid="form-preferred-date" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} className="h-12 mt-2" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address" className="text-sm font-medium">Address (city, parish — or pickup/dropoff for courier)</Label>
                    <Input id="address" data-testid="form-address" value={form.address} onChange={(e) => update("address", e.target.value)} className="h-12 mt-2" placeholder="Metairie, LA  ·  or  pickup → dropoff" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="description" className="text-sm font-medium">Details *</Label>
                    <Textarea id="description" data-testid="form-description" value={form.description} onChange={(e) => update("description", e.target.value)} className="mt-2 min-h-[140px]" placeholder="Brand & model if appliance · what needs doing · timing · or pickup/dropoff details for courier…" />
                    {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start gap-3 p-4 border-2 border-[var(--ji-accent)] bg-[var(--ji-surface-alt)] cursor-pointer hover:border-[var(--ji-primary)] transition-colors" style={{ borderRadius: 4 }} data-testid="form-shop-discount-wrapper">
                      <input
                        type="checkbox"
                        checked={!!form.bought_from_shop}
                        onChange={(e) => update("bought_from_shop", e.target.checked)}
                        data-testid="form-bought-from-shop"
                        className="mt-1 w-5 h-5 accent-[var(--ji-accent)] flex-shrink-0"
                      />
                      <span className="text-sm leading-relaxed">
                        <strong className="text-[var(--ji-primary)]">Buy from our shop → save 10% on install &amp; repair.</strong>
                        <br />
                        <span className="text-[var(--ji-muted)]">Check this box if you purchased the appliance, part, tool, or item from the <a href="/shop" className="text-[var(--ji-primary)] underline hover:text-[var(--ji-accent)]">Joseph Industries shop</a> — your 10% labor discount will be applied automatically to the quote.</span>
                      </span>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="referral_code" className="text-sm font-medium">
                      Referral code <span className="text-[var(--ji-muted)] font-normal">— optional, saves you $25</span>
                    </Label>
                    <Input
                      id="referral_code"
                      data-testid="form-referral-code"
                      value={form.referral_code}
                      onChange={(e) => update("referral_code", e.target.value.toUpperCase())}
                      className="h-12 mt-2 font-mono tracking-wider uppercase"
                      placeholder="e.g. DAVID-A7XZ"
                      maxLength={40}
                    />
                    <p className="text-xs text-[var(--ji-muted)] mt-2 leading-relaxed">
                      Referred by a friend? Enter their code and <strong className="text-[var(--ji-text)]">both of you get $25 off</strong> your next service.{" "}
                      <a href="/refer" className="text-[var(--ji-primary)] underline hover:text-[var(--ji-accent)]">Get your own code →</a>
                    </p>
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={submitting}
                    className="ji-btn-primary inline-flex items-center justify-center gap-2 px-6 h-14 text-base font-semibold disabled:opacity-60"
                    style={{ borderRadius: 4 }}
                  >
                    {submitting ? <><Spinner size={18} className="animate-spin" /> Sending…</> : <>Get my free quote <ArrowRight size={18} weight="bold" /></>}
                  </button>
                  <a href={`tel:${BUSINESS.phones[0].tel}`} data-testid="form-call-cta" className="ji-btn-outline inline-flex items-center justify-center gap-2 px-6 h-14 text-base font-semibold" style={{ borderRadius: 4 }}>
                    <Phone size={18} weight="bold" /> Or call now
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">Direct Lines</div>
            <ul className="space-y-4">
              {BUSINESS.phones.map((p) => (
                <li key={p.tel}>
                  <a href={`tel:${p.tel}`} data-testid={`contact-phone-${p.tel}`} className="group flex items-center justify-between border-b border-[var(--ji-border)] pb-3">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex w-10 h-10 items-center justify-center bg-[var(--ji-surface-alt)] text-[var(--ji-primary)]" style={{ borderRadius: 4 }}>
                        <Phone size={18} weight="bold" />
                      </span>
                      <div>
                        <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold tracking-tight group-hover:text-[var(--ji-accent)]">{p.display}</div>
                        <div className="text-xs text-[var(--ji-muted)] uppercase tracking-[0.2em]">{p.label}</div>
                      </div>
                    </div>
                    <ArrowRight size={16} weight="bold" className="text-[var(--ji-muted)] group-hover:text-[var(--ji-accent)]" />
                  </a>
                </li>
              ))}
            </ul>

            <div className="ji-overline mt-6 mb-3">Email</div>
            <ul className="space-y-2 text-sm">
              {BUSINESS.emails.map((e) => (
                <li key={e}>
                  <a href={`mailto:${e}`} data-testid={`contact-email-${e}`} className="inline-flex items-center gap-2 hover:text-[var(--ji-accent)] break-all">
                    <EnvelopeSimple size={16} weight="bold" className="text-[var(--ji-primary)]" /> {e}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">Hours</div>
            <ul className="space-y-3">
              {BUSINESS.hours.map((h) => (
                <li key={h.day} className="flex items-center justify-between text-sm border-b border-[var(--ji-border)] pb-2 last:border-0">
                  <span className="inline-flex items-center gap-2 text-[var(--ji-muted)]"><Clock size={14} weight="bold" /> {h.day}</span>
                  <span className="font-medium">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">Service Area</div>
            <div className="flex items-start gap-3 text-sm text-[var(--ji-muted)] leading-relaxed">
              <MapPin size={18} weight="duotone" className="text-[var(--ji-accent)] flex-shrink-0 mt-0.5" />
              {BUSINESS.serviceAreaLong}.
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
